import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService, UserAccess } from '../../services/auth.service';
import {
  FileUploadQueueService,
  QuotaBreach,
  UploadEntry,
  UploadEntryState,
} from '../../services/file-upload-queue.service';

/** Ohne Fehler verschwindet die Anzeige nach dieser Zeit von selbst. */
const AUTO_HIDE_MS = 6000;

/**
 * Stand der Datei-Uploads unten rechts — portalweit, solange
 * `FileUploadQueueService` etwas überträgt oder ein Ergebnis zu zeigen hat.
 * Liegt in der Inhaltsspalte (`.al-portal`-Scope), wie `app-portal-hints`.
 */
@Component({
  selector: 'app-upload-tray',
  templateUrl: './upload-tray.component.html',
  styleUrl: './upload-tray.component.scss',
})
export class UploadTrayComponent implements OnInit, OnDestroy {
  entries: UploadEntry[] = [];
  quotaBreach: QuotaBreach = null;
  collapsed = false;

  readonly supportMail = 'support@alphabees.de';

  private sub?: Subscription;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    readonly queue: FileUploadQueueService,
    private authService: AuthService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.sub = this.queue.state$.subscribe((state) => {
      this.entries = state.entries;
      this.quotaBreach = state.quotaBreach;
      this.scheduleAutoHide();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.clearHideTimer();
  }

  get running(): boolean {
    return this.entries.some((e) => e.state === 'queued' || e.state === 'uploading');
  }

  get total(): number {
    return this.entries.length;
  }

  get succeeded(): number {
    return this.entries.filter((e) => e.state === 'success').length;
  }

  get failed(): number {
    return this.entries.filter((e) => e.state === 'error').length;
  }

  get finished(): number {
    return this.entries.filter((e) => e.state !== 'queued' && e.state !== 'uploading').length;
  }

  /** Gesamtfortschritt nach Bytes, damit eine große Datei nicht wie eine kleine zählt. */
  get progress(): number {
    const active = this.entries.filter((e) => e.state !== 'cancelled');
    const bytes = active.reduce((sum, e) => sum + (e.size || 1), 0);
    if (!bytes) return 0;
    const done = active.reduce((sum, e) => {
      const share = e.state === 'success' || e.state === 'error' ? 100 : e.progress;
      return sum + (e.size || 1) * share;
    }, 0);
    return Math.round(done / bytes);
  }

  get canManagePlan(): boolean {
    return this.authService.hasAccess(UserAccess.OrganisationManageSubscription);
  }

  get supportMailHref(): string {
    return `mailto:${this.supportMail}`;
  }

  iconOf(state: UploadEntryState): string {
    switch (state) {
      case 'success':
        return 'checkmark-circle-2-outline';
      case 'error':
        return 'alert-triangle-outline';
      case 'cancelled':
        return 'slash-outline';
      case 'uploading':
        return 'upload-outline';
      default:
        return 'clock-outline';
    }
  }

  trackEntry(_: number, entry: UploadEntry): number {
    return entry.id;
  }

  cancel(): void {
    this.queue.cancelAll();
  }

  close(): void {
    this.clearHideTimer();
    this.queue.clear();
  }

  openPlans(): void {
    this.close();
    this.router.navigate(['/products']);
  }

  private scheduleAutoHide(): void {
    this.clearHideTimer();
    if (!this.entries.length || this.running || this.failed || this.quotaBreach) return;
    this.hideTimer = setTimeout(() => this.close(), AUTO_HIDE_MS);
  }

  private clearHideTimer(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}
